import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import { useCamada } from '@ds/feedback';
import type { AnexoDoBrain } from '@/data/brain';
import { dados } from '@/data/porta';

/**
 * `BrainAttachmentPreview`: miniaturas de imagem e cartões de documento na
 * mensagem, com o visualizador que abre por cima. O endereço é assinado no
 * momento em que a miniatura aparece ou o visualizador abre — curto, porque
 * link encaminhado vaza.
 */

type Tipo = 'image' | 'pdf' | 'sheet' | 'xml' | 'file';

function tipoDoAnexo(a: AnexoDoBrain): Tipo {
    const mime = a.tipo.toLowerCase();
    const nome = a.nome.toLowerCase();
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf' || nome.endsWith('.pdf')) return 'pdf';
    if (/sheet|excel|csv/.test(mime) || /\.(xlsx?|csv|ods)$/.test(nome)) return 'sheet';
    if (mime.includes('xml') || nome.endsWith('.xml')) return 'xml';
    return 'file';
}

function useEnderecoDoAnexo(a: AnexoDoBrain, ligado = true) {
    const [url, setUrl] = useState<string | null>(null);
    const [falhou, setFalhou] = useState(false);
    useEffect(() => {
        if (!ligado) return undefined;
        let vivo = true;
        setFalhou(false);
        dados().brainAbrirArquivo(a.caminho)
            .then((u) => { if (vivo) setUrl(u); })
            .catch(() => { if (vivo) setFalhou(true); });
        return () => { vivo = false; };
    }, [a.caminho, ligado]);
    return { url, falhou };
}

function IconeDoTipo({ tipo, tamanho = 18 }: { tipo: Tipo; tamanho?: number }) {
    if (tipo === 'sheet') return <Icon name="file-spreadsheet" size={tamanho} />;
    if (tipo === 'pdf' || tipo === 'xml') return <Icon name="file-text" size={tamanho} />;
    return <Icon name="file" size={tamanho} />;
}

function Visualizador({ anexo, aoFechar }: { anexo: AnexoDoBrain; aoFechar: () => void }) {
    const tipo = tipoDoAnexo(anexo);
    const { url, falhou } = useEnderecoDoAnexo(anexo);
    const ref = useCamada<HTMLDivElement>({ aberta: true, aoEsc: aoFechar });
    return createPortal(
        <div className="brain-attach-viewer">
            <div className="brain-attach-viewer__backdrop" role="presentation" onClick={aoFechar} />
            <div ref={ref} className="brain-attach-viewer__panel" role="dialog" aria-modal="true" aria-label={anexo.nome} tabIndex={-1}>
                <div className="brain-attach-viewer__header">
                    <span className="brain-attach-viewer__title" title={anexo.nome}>{anexo.nome}</span>
                    {url && (
                        <>
                            <a className="brain-attach-viewer__btn" href={url} target="_blank" rel="noopener noreferrer" aria-label="Abrir em nova aba">
                                <Icon name="external-link" size={18} />
                            </a>
                            <a className="brain-attach-viewer__btn" href={url} download={anexo.nome} aria-label="Baixar">
                                <Icon name="download" size={18} />
                            </a>
                        </>
                    )}
                    <button type="button" className="brain-attach-viewer__btn" onClick={aoFechar} aria-label="Fechar">
                        <Icon name="x" size={20} />
                    </button>
                </div>
                <div className="brain-attach-viewer__body">
                    {falhou ? (
                        <div className="brain-attach-viewer__message">Não foi possível abrir este arquivo.</div>
                    ) : !url ? (
                        <div className="brain-attach-viewer__message">Carregando…</div>
                    ) : tipo === 'image' ? (
                        <img className="brain-attach-viewer__image" src={url} alt={anexo.nome} />
                    ) : tipo === 'pdf' ? (
                        <iframe className="brain-attach-viewer__frame" src={url} title={anexo.nome} />
                    ) : (
                        <div className="brain-attach-viewer__message">
                            <IconeDoTipo tipo={tipo} tamanho={36} />
                            <div>{anexo.nome}</div>
                            <div>Pré-visualização indisponível para este formato. Use o botão de baixar.</div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

function Miniatura({ anexo, aoAbrir }: { anexo: AnexoDoBrain; aoAbrir: () => void }) {
    const { url, falhou } = useEnderecoDoAnexo(anexo);
    return (
        <button type="button" className="brain-attach-preview__thumb" onClick={aoAbrir} aria-label={`Ver ${anexo.nome}`} title={anexo.nome}>
            {url && !falhou ? (
                <img src={url} alt="" loading="lazy" />
            ) : (
                <span className="brain-attach-preview__thumb-placeholder"><Icon name="file" size={18} /></span>
            )}
        </button>
    );
}

export function PrevisaoDosAnexos({ anexos }: { anexos: AnexoDoBrain[] }) {
    const [aberto, setAberto] = useState<AnexoDoBrain | null>(null);
    if (anexos.length === 0) return null;
    return (
        <div className="brain-attach-preview">
            {anexos.map((a) => {
                const tipo = tipoDoAnexo(a);
                if (tipo === 'image') return <Miniatura key={a.caminho} anexo={a} aoAbrir={() => setAberto(a)} />;
                return (
                    <button key={a.caminho} type="button" className="brain-attach-preview__card" onClick={() => setAberto(a)} title={a.nome} aria-label={`Abrir o anexo ${a.nome}`}>
                        <span className={`brain-attach-preview__card-icon brain-attach-preview__card-icon--${tipo}`}><IconeDoTipo tipo={tipo} /></span>
                        <span className="brain-attach-preview__card-body">
                            <span className="brain-attach-preview__card-name">{a.nome}</span>
                            <span className="brain-attach-preview__card-meta">{tipo === 'file' ? 'Arquivo' : tipo.toUpperCase()}</span>
                        </span>
                    </button>
                );
            })}
            {aberto && <Visualizador anexo={aberto} aoFechar={() => setAberto(null)} />}
        </div>
    );
}
